import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Stethoscope, Video, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import RescheduleModal from "../components/RescheduleModal";
import { api, formatApiErrorDetail } from "../lib/api";

const FALLBACK_DOCTOR_IMG =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=400&q=80";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
}

const STATUS_STYLES = {
  upcoming: "bg-accent text-accent-foreground",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

function hoursUntil(iso) {
  return (new Date(iso).getTime() - Date.now()) / 1000 / 3600;
}

function AppointmentCard({ a, onCancelled, onRescheduled, navigate }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const hUntil = hoursUntil(a.scheduled_at);
  const canModify = a.status === "upcoming" && hUntil >= 2;
  const canJoinVideo =
    (a.status === "upcoming" || a.status === "in_progress") && a.mode === "video";

  const handleCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/appointments/${a.id}/cancel`, { reason: "patient_cancelled" });
      toast.success("Appointment cancelled and refund initiated");
      setCancelOpen(false);
      onCancelled?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`appt-card-${a.id}`}
      className="bg-white border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <img
          src={a.doctor?.image_url || FALLBACK_DOCTOR_IMG}
          alt={a.doctor?.name}
          className="h-14 w-14 rounded-xl object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{a.doctor?.name}</span>
            <span
              data-testid={`appt-status-${a.id}`}
              className={"text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " + (STATUS_STYLES[a.status] || "bg-muted text-muted-foreground")}
            >
              {a.status}
            </span>
            <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground inline-flex items-center gap-1">
              {a.mode === "video" ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
              {a.mode}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {a.doctor?.category}
          </div>
          <div className="text-sm mt-1 inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {fmt(a.scheduled_at)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {a.id} · ₹{a.fee}
          </div>
        </div>

        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <Link
            to={`/patient/appointments/${a.id}`}
            data-testid={`appt-details-link-${a.id}`}
            className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline"
          >
            Details <ChevronRight className="h-3.5 w-3.5" />
          </Link>
          {(a.status === "upcoming" || a.status === "in_progress") && (
            <div className="flex gap-2">
              {a.mode === "video" && (
                <Button
                  data-testid={`appt-join-video-${a.id}`}
                  size="sm"
                  variant="default"
                  onClick={() => navigate(`/patient/consult/${a.id}`)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                >
                  Join Video
                </Button>
              )}
              <Button
                data-testid={`appt-reschedule-${a.id}`}
                size="sm"
                variant="outline"
                disabled={!canModify}
                onClick={() => setRescheduleOpen(true)}
                className="rounded-full"
              >
                Reschedule
              </Button>
              <Button
                data-testid={`appt-cancel-${a.id}`}
                size="sm"
                variant="outline"
                disabled={!canModify}
                onClick={() => setCancelOpen(true)}
                className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid={`cancel-confirm-${a.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll be refunded ₹{a.fee} to your original payment method. Appointments can
              only be cancelled at least 2 hours before the scheduled time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`cancel-no-${a.id}`}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              data-testid={`cancel-yes-${a.id}`}
              onClick={handleCancel}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {busy ? "Cancelling…" : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {rescheduleOpen && (
        <RescheduleModal
          appointment={a}
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          onDone={() => {
            setRescheduleOpen(false);
            onRescheduled?.();
          }}
        />
      )}
    </div>
  );
}

export default function PatientAppointments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/mine?status=${tab}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="patient-appointments-page"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-5xl w-full mx-auto space-y-6"
      >
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">My appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage upcoming, past and cancelled consults.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-secondary p-1 rounded-full h-12">
            <TabsTrigger
              value="upcoming"
              data-testid="appts-tab-upcoming"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Upcoming
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              data-testid="appts-tab-completed"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Completed
            </TabsTrigger>
            <TabsTrigger
              value="cancelled"
              data-testid="appts-tab-cancelled"
              className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Cancelled
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-6 space-y-3">
            {loading ? (
              [1, 2].map((i) => (
                <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />
              ))
            ) : items.length === 0 ? (
              <div
                data-testid="appts-empty-state"
                className="bg-white border border-border rounded-2xl p-10 text-center"
              >
                <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <div className="font-semibold text-lg mb-1">
                  {tab === "upcoming"
                    ? "No upcoming appointments"
                    : tab === "completed"
                      ? "No completed appointments yet"
                      : "No cancelled appointments"}
                </div>
                {tab === "upcoming" && (
                  <>
                    <div className="text-sm text-muted-foreground mb-4">
                      Book your first consultation in under a minute.
                    </div>
                    <Button
                      data-testid="empty-find-doctor-btn"
                      onClick={() => navigate("/patient/doctors")}
                      className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                    >
                      Find a doctor
                    </Button>
                  </>
                )}
              </div>
            ) : (
              items.map((a) => (
                <AppointmentCard
                  key={a.id}
                  a={a}
                  navigate={navigate}
                  onCancelled={load}
                  onRescheduled={load}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
